import React, { useEffect, useMemo, useState } from "react";
import client from "../../api/client";
import { Layout } from "../../components/Layout/Layout";
import User from "../../components/User";
import { useInHouse } from "../../context/inHousePermissions";
import { formatRelative } from "../../utils/formatRelative";
import { EXPRESS_PRICE_KZT } from "../../utils/expressHelpers";
import { hasElevatedRoute } from "../../utils/roleAccess";
// Переиспользуем стили страницы продаж (.tbl, .modal, .btn, .sales-* и т.д.).
import "../sales/index.css";
import "./index.css";

interface Transaction {
  id: number;
  userId: number | null;
  username: string | null;
  manager: string;
  amount: number;
  currency: string;
  bank: string;
  paymentType: string;
  receipt: string;
  createdAt: string;
  orderStatus: string | null;
  promoCode: string | null;
}

interface StaffLite {
  id: number;
  name: string;
  role: string;
}

interface UserHit {
  id: number;
  username: string;
}

// Все типы транзакций — только для подписи в таблице.
const PAYMENT_TYPE_LABELS: Record<string, string> = {
  full_payment: "Полная оплата",
  prepayment: "Предоплата",
  postpayment: "Постоплата",
  express_print: "Экспресс печать",
  express_edit: "Ускоренная редактура",
  urep: "УРЭП",
  delivery: "Доставка",
  additional: "Прочие",
};
const paymentTypeLabel = (v: string) => PAYMENT_TYPE_LABELS[v] || v;

// Какие типы может создавать каждая роль (должно совпадать с
// CREATABLE_BY_ROLE на бэке — backend/src/routes/transactions.ts).
const SECONDARY_TYPES = [
  "postpayment",
  "express_print",
  "express_edit",
  "urep",
  "delivery",
  "additional",
];
const CREATABLE_BY_ROLE: Record<string, string[]> = {
  admin: SECONDARY_TYPES,
  operations: SECONDARY_TYPES,
  editor: ["express_print", "express_edit", "urep", "additional"],
  log: ["delivery"],
  marketing: ["additional"],
};

// Роли, видящие все транзакции. Персонально выданный /transactions
// (staff.extraRoutes) приравнивается к ним — так же считает бэк.
const ALL_SEERS = ["admin", "operations"];

// Счета — значения bank enum из backend schema.ts.
const BANKS = [
  "kaspi",
  "kaspi_gold",
  "kaspi_pay",
  "vtb",
  "tinkoff",
  "optima",
  "halyk",
  "stripe",
  "cash",
  "other",
];

const CURRENCIES = ["KZT", "USD", "AZN", "BYN", "GEL", "TRY", "RUB", "EUR", "KGS"];

const Transactions: React.FC = () => {
  const { currentUserRecord } = useInHouse();
  const role = currentUserRecord?.role || "";
  const elevated = hasElevatedRoute(
    currentUserRecord?.extraRoutes,
    "/transactions"
  );
  const isAllSeer = ALL_SEERS.includes(role) || elevated;
  const creatableTypes = elevated
    ? SECONDARY_TYPES
    : CREATABLE_BY_ROLE[role] || [];

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState<StaffLite[]>([]);
  const [filter, setFilter] = useState(""); // имя сотрудника, "" = все

  // Tick раз в минуту, чтобы «X мин назад» оставалось свежим.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Попап User.tsx по клику на строку.
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [rowLoadingId, setRowLoadingId] = useState<number | null>(null);

  // Кнопки-фильтры со всеми сотрудниками (только для admin/operations).
  useEffect(() => {
    if (!isAllSeer) return;
    client
      .get<StaffLite[]>("/transactions/staff")
      .then((r) =>
        setStaffList(
          [...r.data].sort((a, b) => a.name.localeCompare(b.name, "ru"))
        )
      )
      .catch(() => {});
  }, [isAllSeer]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params: any = { limit: 300 };
      // Фильтр по сотруднику шлём только all-seer'ам; остальным бэк всё равно
      // вернёт только их транзакции.
      if (isAllSeer && filter) params.manager = filter;
      const { data } = await client.get("/transactions", { params });
      setTransactions(data);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, isAllSeer]);

  const handleRowClick = async (t: Transaction) => {
    if (!t.username) return;
    if (rowLoadingId) return;
    setRowLoadingId(t.id);
    try {
      const { data } = await client.get(`/orders/${t.username}`);
      setSelectedUser({ username: t.username, ...data });
    } catch (err) {
      console.error("Failed to load order for transaction", t.id, err);
    } finally {
      setRowLoadingId(null);
    }
  };

  // ── Создание транзакции ──
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Поиск юзера
  const [userQuery, setUserQuery] = useState("");
  const [userHits, setUserHits] = useState<UserHit[]>([]);
  const [selectedTxUser, setSelectedTxUser] = useState<UserHit | null>(null);

  const [txType, setTxType] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("KZT");
  const [bank, setBank] = useState("kaspi");
  const [receipt, setReceipt] = useState("");

  // Дебаунс-поиск юзера (как в сайдбаре).
  useEffect(() => {
    const q = userQuery.trim();
    if (!q || selectedTxUser) {
      setUserHits([]);
      return;
    }
    const t = window.setTimeout(async () => {
      try {
        const { data } = await client.get<UserHit[]>("/admin/users/search", {
          params: { q, limit: 5 },
        });
        setUserHits(Array.isArray(data) ? data : []);
      } catch {
        setUserHits([]);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [userQuery, selectedTxUser]);

  const openCreate = () => {
    setSelectedTxUser(null);
    setUserQuery("");
    setUserHits([]);
    const initialType = creatableTypes[0] || "";
    setTxType(initialType);
    // Если стартовый тип — экспресс-услуга, сразу подставляем цену.
    const initialPrice = (EXPRESS_PRICE_KZT as Record<string, number>)[initialType];
    setAmount(initialPrice ? String(initialPrice) : "");
    setCurrency("KZT");
    setBank("kaspi");
    setReceipt("");
    setMsg(null);
    setShowCreate(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTxUser) {
      setMsg({ ok: false, text: "Выберите юзера" });
      return;
    }
    if (!txType) {
      setMsg({ ok: false, text: "Выберите тип транзакции" });
      return;
    }
    setCreating(true);
    setMsg(null);
    try {
      await client.post("/transactions", {
        userId: selectedTxUser.id,
        amount: Number(amount) || 0,
        currency,
        bank,
        paymentType: txType,
        receipt,
      });
      setMsg({
        ok: true,
        text: `Транзакция для ${selectedTxUser.username} создана`,
      });
      fetchTransactions();
      // Сбрасываем юзера/сумму, тип/счёт оставляем — удобно вносить пачкой.
      setSelectedTxUser(null);
      setUserQuery("");
      setAmount("");
      setReceipt("");
    } catch (err: any) {
      setMsg({
        ok: false,
        text: err.response?.data?.error || "Ошибка",
      });
    } finally {
      setCreating(false);
    }
  };

  const creatableOptions = useMemo(
    () => creatableTypes.map((t) => ({ value: t, label: paymentTypeLabel(t) })),
    [creatableTypes]
  );

  return (
    <Layout>
      <div className="sales-head">
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>Транзакции</h2>
        {creatableTypes.length > 0 && (
          <button className="btn" onClick={openCreate}>
            + Создать транзакцию
          </button>
        )}
      </div>

      {isAllSeer && (
        <div className="filters sales-mgr-filter">
          <button
            type="button"
            className={`sales-mgr-btn${filter === "" ? " sales-mgr-btn--active" : ""}`}
            onClick={() => setFilter("")}
          >
            Все
          </button>
          {staffList.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`sales-mgr-btn${filter === s.name ? " sales-mgr-btn--active" : ""}`}
              onClick={() => setFilter(s.name)}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ color: "var(--text-3)" }}>Загрузка...</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Тип</th>
              <th>Юзер</th>
              <th>Сумма</th>
              <th>Счет</th>
              <th>Сотрудник</th>
              <th>Дата</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((t) => (
              <tr
                key={t.id}
                className={t.username ? "tbl__row--clickable" : undefined}
                style={{ opacity: rowLoadingId === t.id ? 0.5 : undefined }}
                onClick={() => handleRowClick(t)}
              >
                <td>{paymentTypeLabel(t.paymentType)}</td>
                <td>{t.username || "—"}</td>
                <td>
                  {t.amount?.toLocaleString()} {t.currency}
                </td>
                <td>{t.bank || "—"}</td>
                <td>{t.manager || "—"}</td>
                <td>{formatRelative(t.createdAt)}</td>
              </tr>
            ))}
            {!transactions.length && (
              <tr>
                <td
                  colSpan={6}
                  style={{ textAlign: "center", color: "var(--text-3)" }}
                >
                  Нет транзакций
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {selectedUser && (
        <User
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdate={() => fetchTransactions()}
          deadline="—"
        />
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
                Создать транзакцию
              </h3>
              <button
                className="modal-close"
                onClick={() => setShowCreate(false)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            {msg && (
              <div className={`msg ${msg.ok ? "msg--ok" : "msg--err"}`}>
                {msg.text}
              </div>
            )}

            <form className="form" onSubmit={handleCreate}>
              <div className="field">
                <label>Юзер</label>
                {selectedTxUser ? (
                  <div className="pw-row">
                    <input value={selectedTxUser.username} readOnly />
                    <button
                      type="button"
                      className="gen-btn"
                      onClick={() => {
                        setSelectedTxUser(null);
                        setUserQuery("");
                      }}
                    >
                      Сменить
                    </button>
                  </div>
                ) : (
                  <div style={{ position: "relative" }}>
                    <input
                      value={userQuery}
                      onChange={(e) => setUserQuery(e.target.value)}
                      placeholder="Поиск по username…"
                    />
                    {userQuery.trim() && userHits.length > 0 && (
                      <div className="tx-search-dropdown">
                        {userHits.map((h) => (
                          <div
                            key={h.id}
                            className="tx-search-item"
                            onClick={() => {
                              setSelectedTxUser(h);
                              setUserHits([]);
                            }}
                          >
                            {h.username}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="field">
                <label>Тип транзакции</label>
                <select
                  value={txType}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTxType(v);
                    // Авто-цена для экспресс-услуг (KZT). Сумму и валюту
                    // ставим автоматом, чтобы менеджер не считал руками;
                    // при желании всегда можно поправить.
                    const price = (EXPRESS_PRICE_KZT as Record<string, number>)[v];
                    if (price) {
                      setAmount(String(price));
                      setCurrency("KZT");
                    }
                  }}
                >
                  {creatableOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="field">
                  <label>Сумма</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="field">
                  <label>Валюта</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field">
                <label>Счет</label>
                <select value={bank} onChange={(e) => setBank(e.target.value)}>
                  {BANKS.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Чек / комментарий (опц.)</label>
                <input
                  value={receipt}
                  onChange={(e) => setReceipt(e.target.value)}
                  placeholder="Необязательно"
                />
              </div>

              <button type="submit" className="btn" disabled={creating}>
                {creating ? "Создание..." : "Создать"}
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Transactions;
